import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import useGlobalContext from "hooks/globalData";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  ListRequestOfResourceFilterV2,
  ListResponseOfResourceV2ListItem,
  ResourceFilterV2,
  ResourceV2ListItem,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";

type SelectResourceProps = {
  value?: ResourceV2ListItem | null;
  filter?: {
    skuId?: string | undefined;
    isLinkedWithUser?: boolean;
    hasAnySkuUserAgreement?: boolean;
  };
  onChange: (value: ResourceV2ListItem | null) => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<ResourceV2ListItem>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectResource = ({
  filter,
  value,
  onChange,
  ...rest
}: SelectResourceProps) => {
  const { formatMessage: t } = useIntl();
  const { resourceService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [resource, setResource] = useState<ResourceV2ListItem | null>(null);
  const { organizationId } = contextStore.useState();

  // Load the initial value if we're passed a resourceId
  const { isLoading, data } = useQuery(
    ["resourceById", value?.id, value?.organizationUserId, filter?.skuId],
    () =>
      resourceService.getResources(
        new ListRequestOfResourceFilterV2({
          parameters: new ResourceFilterV2({
            includedIds: value?.id ? [value.id] : undefined,
            organizationUserId: value?.organizationUserId,
            skuId: filter?.skuId,
          }),
        })
      ),
    {
      enabled: (!!value?.id || !!value?.organizationUserId) && !resource,
    }
  );

  const onChangeResource = useCallback((resource: ResourceV2ListItem | null) => {
    setResource(resource);
    onChange(resource ?? null);
  },[onChange]);

  useEffect(() => {
    if (data?.data?.[0]) {
      onChangeResource(data.data[0]);
    }
  }, [data, onChangeResource]);

  const fetchResources = async (searchTerm: string) => {
    const result = await queryClient.fetchQuery<ListResponseOfResourceV2ListItem>(
      ["resources", searchTerm, organizationId, filter?.hasAnySkuUserAgreement, filter?.isLinkedWithUser, filter?.skuId],
      () =>
        resourceService.getResources(
          new ListRequestOfResourceFilterV2({
            pageNumber: 0,
            pageSize: 20,
            parameters: ResourceFilterV2.fromJS({
              searchText: searchTerm === "" ? undefined : searchTerm,
              ...filter,
            }),
            sortField: "value",
          })
        )
    );

    const resources = {
      ...result,
      data: result.data ?? [],
    };

    return { result: resources, forSearchTerm: searchTerm };
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={resource}
      isLoadingInitialValue={isLoading}
      labelField="name"
      idField="id"
      label={t({ id: "skuFacultyTitle" })}
      fetchData={fetchResources}
      failMessage={t({ id: "skuFacultyTitle" })}
      onChange={onChangeResource}
    />
  );
};

export default SelectResource;
